import React, { useEffect, useState, useMemo } from 'react';
import {
  Box, Typography, Tabs, Tab, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, Button, Chip, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, Select, MenuItem,
  FormControl, InputLabel, IconButton, Tooltip, Menu, Alert, CircularProgress,
  InputAdornment, Stack, useMediaQuery, useTheme,
  Accordion, AccordionSummary, AccordionDetails,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import SearchIcon from '@mui/icons-material/Search';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import EmailIcon from '@mui/icons-material/Email';
import {
  getConsultationTemplates, createConsultationTemplate, updateConsultationTemplate,
  deleteConsultationTemplate, getWhatsAppTemplates, createWhatsAppTemplate,
  updateWhatsAppTemplate, deleteWhatsAppTemplate, getStudyOrderTemplates,
  createStudyOrderTemplate, updateStudyOrderTemplate, deleteStudyOrderTemplate,
  getEmailTemplates, updateEmailTemplate, resetEmailTemplate,
} from '../services/api';
import { useAuth } from '../hooks/useAuth';
import { useApiCall } from '../hooks/useApiCall';
import { useClinicFeatures } from '../hooks/useClinicFeatures';
import { useNotification } from '../hooks/useNotification';
import RichTextEditor from '../components/RichTextEditor';

// ── Campos del contenido de plantilla clínica ─────────────────────────────

const CONTENT_FIELDS = [
  { key: 'consultation_type', label: 'Tipo de consulta' },
  { key: 'notes', label: 'Evolución' },
];

const parseContent = (str) => {
  try { return JSON.parse(str || '{}'); } catch { return {}; }
};

// ── Plantillas de consulta ─────────────────────────────────────────────────

const emptyConsult = { name: '', specialty_tag: '', scope: 'personal', content: {} };

const ConsultationTemplates = () => {
  const { isAdmin } = useAuth();
  const apiCall = useApiCall();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyConsult);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');

  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuRow, setMenuRow] = useState(null);
  const openMenu = (e, t) => { setMenuAnchor(e.currentTarget); setMenuRow(t); };
  const closeMenu = () => { setMenuAnchor(null); setMenuRow(null); };

  const load = async () => {
    setLoading(true);
    try { const r = await getConsultationTemplates(); setTemplates(r.data || []); }
    catch { setTemplates([]); }
    finally { setLoading(false); }
  };

  const filteredTemplates = useMemo(() => {
    if (!search.trim()) return templates;
    const q = search.trim().toLowerCase();
    return templates.filter(t =>
      t.name.toLowerCase().includes(q) || (t.specialty_tag || '').toLowerCase().includes(q)
    );
  }, [templates, search]);

  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyConsult);
    setError('');
    setOpen(true);
  };

  const openEdit = (t) => {
    closeMenu();
    setEditing(t);
    setForm({ name: t.name, specialty_tag: t.specialty_tag || '', scope: t.scope, content: parseContent(t.content) });
    setError('');
    setOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) { setError('El nombre es obligatorio'); return; }
    setSaving(true);
    setError('');
    try {
      const payload = { name: form.name, specialty_tag: form.specialty_tag || null, scope: form.scope, content: JSON.stringify(form.content) };
      if (editing) await updateConsultationTemplate(editing.id, payload);
      else await createConsultationTemplate(payload);
      setOpen(false);
      await load();
    } catch (err) {
      setError(err.userMessage || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (t) => {
    closeMenu();
    if (!window.confirm(`¿Eliminar plantilla "${t.name}"?`)) return;
    try { await apiCall(deleteConsultationTemplate(t.id), 'Error al eliminar'); await load(); }
    catch {}
  };

  const setContentField = (key) => (e) =>
    setForm(p => ({ ...p, content: { ...p.content, [key]: e.target.value } }));
  // RichTextEditor entrega el HTML directo (no un evento) — ver su firma
  // en components/RichTextEditor.js. El campo "notes" se guarda como el
  // mismo HTML que después sanitizeNotesHtml procesa en NewRecordForm al
  // aplicar la plantilla, igual que una consulta cargada a mano.
  const setNotesField = (html) =>
    setForm(p => ({ ...p, content: { ...p.content, notes: html } }));

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <Button variant="contained" onClick={openCreate}>Nueva plantilla</Button>
      </Box>

      <Paper sx={{ p: 1.5, mb: 2 }}>
        <TextField
          placeholder="Buscar por nombre o especialidad…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          size="small"
          fullWidth
          InputProps={{
            startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: 'text.disabled', fontSize: 20 }} /></InputAdornment>,
          }}
        />
      </Paper>

      {loading ? (
        <CircularProgress />
      ) : filteredTemplates.length === 0 ? (
        <Paper sx={{ py: 6, textAlign: 'center' }}>
          <Typography color="text.secondary">
            {search ? 'Sin plantillas que coincidan con la búsqueda' : 'Sin plantillas'}
          </Typography>
        </Paper>
      ) : isMobile ? (
        <Stack spacing={1.5}>
          {filteredTemplates.map(t => (
            <Paper key={t.id} sx={{ p: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                    <Typography variant="body2" fontWeight={600}>{t.name}</Typography>
                    <Chip
                      label={t.scope === 'organizational' ? 'Organizacional' : 'Personal'}
                      color={t.scope === 'organizational' ? 'primary' : 'default'}
                      size="small" sx={{ height: 20, fontSize: '0.68rem' }}
                    />
                  </Box>
                  {t.specialty_tag && <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{t.specialty_tag}</Typography>}
                </Box>
                <IconButton size="small" onClick={(e) => openMenu(e, t)} sx={{ flexShrink: 0 }}>
                  <MoreVertIcon fontSize="small" />
                </IconButton>
              </Box>
            </Paper>
          ))}
        </Stack>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Nombre</TableCell>
                <TableCell>Especialidad</TableCell>
                <TableCell>Alcance</TableCell>
                <TableCell align="right" sx={{ width: 60 }}></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredTemplates.map(t => (
                <TableRow key={t.id} hover>
                  <TableCell>{t.name}</TableCell>
                  <TableCell>{t.specialty_tag || '-'}</TableCell>
                  <TableCell>
                    <Chip
                      label={t.scope === 'organizational' ? 'Organizacional' : 'Personal'}
                      color={t.scope === 'organizational' ? 'primary' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Acciones">
                      <IconButton size="small" onClick={(e) => openMenu(e, t)}>
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={closeMenu}
        PaperProps={{ sx: { minWidth: 160 } }}>
        <MenuItem dense onClick={() => openEdit(menuRow)}>Editar</MenuItem>
        <MenuItem dense sx={{ color: 'error.main' }} onClick={() => handleDelete(menuRow)}>Eliminar</MenuItem>
      </Menu>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? 'Editar plantilla' : 'Nueva plantilla de consulta'}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField label="Nombre *" fullWidth value={form.name}
            onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
            error={!form.name.trim() && !!error} />
          <TextField label="Especialidad (opcional)" fullWidth value={form.specialty_tag}
            onChange={e => setForm(p => ({ ...p, specialty_tag: e.target.value }))} />
          {isAdmin && (
            <FormControl fullWidth>
              <InputLabel>Alcance</InputLabel>
              <Select value={form.scope} label="Alcance" onChange={e => setForm(p => ({ ...p, scope: e.target.value }))}>
                <MenuItem value="personal">Personal (solo vos)</MenuItem>
                <MenuItem value="organizational">Organizacional (toda la clínica)</MenuItem>
              </Select>
            </FormControl>
          )}
          <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1 }}>
            Contenido por defecto
          </Typography>
          {CONTENT_FIELDS.map(({ key, label }) => key === 'notes' ? (
            // Antes "Evolución" era un TextField de texto plano acá —
            // sin negrita/color/subrayado aunque el editor real de la
            // consulta (NewRecordForm) sí los soporta. El HTML que arma
            // RichTextEditor es el mismo que después aplica sin cambios
            // al crear una consulta desde esta plantilla. Reportado por
            // el usuario 2026-10-12.
            <Box key={key}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                {label}
              </Typography>
              <RichTextEditor value={form.content.notes || ''} onChange={setNotesField} minRows={6} />
            </Box>
          ) : (
            <TextField key={key} label={label} fullWidth multiline rows={2}
              value={form.content[key] || ''} onChange={setContentField(key)} />
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? <CircularProgress size={20} /> : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// ── Plantillas de órdenes de estudio ──────────────────────────────────────
// A diferencia de ConsultationTemplates (content = objeto de campos del
// formulario de consulta), acá content es una LISTA de items de estudio —
// "aplicar" la plantilla agrega filas a una orden, no hace merge de campos.
// Ver OrdenesTab.js:applyTemplate.

const SO_CATEGORY_OPTIONS = [
  { value: 'laboratorio', label: 'Laboratorio' },
  { value: 'imagen', label: 'Imagen' },
  { value: 'otro', label: 'Otro' },
];

const emptySOItem = () => ({ study_name: '', category: 'laboratorio', instructions: '' });
const emptyStudyOrderTemplate = { name: '', scope: 'personal', items: [emptySOItem()] };

const parseSOItems = (str) => {
  try {
    const parsed = JSON.parse(str || '[]');
    return Array.isArray(parsed) && parsed.length > 0 ? parsed : [emptySOItem()];
  } catch { return [emptySOItem()]; }
};

const StudyOrderTemplates = () => {
  const { isAdmin } = useAuth();
  const apiCall = useApiCall();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyStudyOrderTemplate);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');

  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuRow, setMenuRow] = useState(null);
  const openMenu = (e, t) => { setMenuAnchor(e.currentTarget); setMenuRow(t); };
  const closeMenu = () => { setMenuAnchor(null); setMenuRow(null); };

  const load = async () => {
    setLoading(true);
    try { const r = await getStudyOrderTemplates(); setTemplates(r.data || []); }
    catch { setTemplates([]); }
    finally { setLoading(false); }
  };

  const filteredTemplates = useMemo(() => {
    if (!search.trim()) return templates;
    const q = search.trim().toLowerCase();
    return templates.filter(t => t.name.toLowerCase().includes(q));
  }, [templates, search]);

  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyStudyOrderTemplate);
    setError('');
    setOpen(true);
  };

  const openEdit = (t) => {
    closeMenu();
    setEditing(t);
    setForm({ name: t.name, scope: t.scope, items: parseSOItems(t.content) });
    setError('');
    setOpen(true);
  };

  const updateItem = (idx, field, value) =>
    setForm(p => ({ ...p, items: p.items.map((it, i) => i === idx ? { ...it, [field]: value } : it) }));

  const handleSave = async () => {
    if (!form.name.trim()) { setError('El nombre es obligatorio'); return; }
    const validItems = form.items.filter(i => i.study_name.trim());
    if (validItems.length === 0) { setError('Agregá al menos un estudio'); return; }
    setSaving(true);
    setError('');
    try {
      const payload = { name: form.name, scope: form.scope, content: JSON.stringify(validItems) };
      if (editing) await updateStudyOrderTemplate(editing.id, payload);
      else await createStudyOrderTemplate(payload);
      setOpen(false);
      await load();
    } catch (err) {
      setError(err.userMessage || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (t) => {
    closeMenu();
    if (!window.confirm(`¿Eliminar plantilla "${t.name}"?`)) return;
    try { await apiCall(deleteStudyOrderTemplate(t.id), 'Error al eliminar'); await load(); }
    catch {}
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <Button variant="contained" onClick={openCreate}>Nueva plantilla</Button>
      </Box>

      <Paper sx={{ p: 1.5, mb: 2 }}>
        <TextField
          placeholder="Buscar por nombre…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          size="small"
          fullWidth
          InputProps={{
            startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: 'text.disabled', fontSize: 20 }} /></InputAdornment>,
          }}
        />
      </Paper>

      {loading ? (
        <CircularProgress />
      ) : filteredTemplates.length === 0 ? (
        <Paper sx={{ py: 6, textAlign: 'center' }}>
          <Typography color="text.secondary">
            {search ? 'Sin plantillas que coincidan con la búsqueda' : 'Sin plantillas'}
          </Typography>
        </Paper>
      ) : isMobile ? (
        <Stack spacing={1.5}>
          {filteredTemplates.map(t => {
            const items = parseSOItems(t.content);
            return (
              <Paper key={t.id} sx={{ p: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                      <Typography variant="body2" fontWeight={600}>{t.name}</Typography>
                      <Chip
                        label={t.scope === 'organizational' ? 'Organizacional' : 'Personal'}
                        color={t.scope === 'organizational' ? 'primary' : 'default'}
                        size="small" sx={{ height: 20, fontSize: '0.68rem' }}
                      />
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {items.map(i => i.study_name).join(', ')}
                    </Typography>
                  </Box>
                  <IconButton size="small" onClick={(e) => openMenu(e, t)} sx={{ flexShrink: 0 }}>
                    <MoreVertIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Paper>
            );
          })}
        </Stack>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Nombre</TableCell>
                <TableCell>Estudios</TableCell>
                <TableCell>Alcance</TableCell>
                <TableCell align="right" sx={{ width: 60 }}></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredTemplates.map(t => {
                const items = parseSOItems(t.content);
                return (
                  <TableRow key={t.id} hover>
                    <TableCell>{t.name}</TableCell>
                    <TableCell sx={{ maxWidth: 320 }}>
                      <Typography variant="caption" noWrap sx={{ display: 'block' }}>
                        {items.map(i => i.study_name).join(', ')}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={t.scope === 'organizational' ? 'Organizacional' : 'Personal'}
                        color={t.scope === 'organizational' ? 'primary' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Acciones">
                        <IconButton size="small" onClick={(e) => openMenu(e, t)}>
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={closeMenu}
        PaperProps={{ sx: { minWidth: 160 } }}>
        <MenuItem dense onClick={() => openEdit(menuRow)}>Editar</MenuItem>
        <MenuItem dense sx={{ color: 'error.main' }} onClick={() => handleDelete(menuRow)}>Eliminar</MenuItem>
      </Menu>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? 'Editar plantilla' : 'Nueva plantilla de orden de estudio'}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField label="Nombre *" fullWidth value={form.name}
            onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
            error={!form.name.trim() && !!error} />
          {isAdmin && (
            <FormControl fullWidth>
              <InputLabel>Alcance</InputLabel>
              <Select value={form.scope} label="Alcance" onChange={e => setForm(p => ({ ...p, scope: e.target.value }))}>
                <MenuItem value="personal">Personal (solo vos)</MenuItem>
                <MenuItem value="organizational">Organizacional (toda la clínica)</MenuItem>
              </Select>
            </FormControl>
          )}
          <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1 }}>
            Estudios
          </Typography>
          {form.items.map((item, idx) => (
            <Box key={idx} sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'flex-start' }}>
              <TextField label="Estudio *" size="small" sx={{ flex: '2 1 180px' }}
                value={item.study_name} onChange={e => updateItem(idx, 'study_name', e.target.value)} />
              <FormControl size="small" sx={{ flex: '1 1 130px' }}>
                <InputLabel>Categoría</InputLabel>
                <Select value={item.category} label="Categoría" onChange={e => updateItem(idx, 'category', e.target.value)}>
                  {SO_CATEGORY_OPTIONS.map(c => <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>)}
                </Select>
              </FormControl>
              <TextField label="Indicaciones" size="small" sx={{ flex: '2 1 180px' }}
                value={item.instructions} onChange={e => updateItem(idx, 'instructions', e.target.value)} />
              {form.items.length > 1 && (
                <IconButton size="small" color="error"
                  onClick={() => setForm(p => ({ ...p, items: p.items.filter((_, i) => i !== idx) }))}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              )}
            </Box>
          ))}
          <Button size="small" onClick={() => setForm(p => ({ ...p, items: [...p.items, emptySOItem()] }))}>
            Agregar estudio
          </Button>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? <CircularProgress size={20} /> : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// ── Plantillas de email (confirmación/recordatorio/cancelación de turno) ────
// A diferencia de las otras 3 pestañas, acá no hay CRUD de "crear/eliminar
// varias plantillas" — el envío es automático (crear/cancelar turno, cron
// de recordatorios) y siempre hay exactamente 3 tipos fijos. La UI es una
// lista de 3 acordeones (uno por tipo) en vez de tabla+dialog.

const EMAIL_TEMPLATE_LABELS = {
  confirmacion_turno: 'Confirmación de turno',
  recordatorio_turno: 'Recordatorio de turno (24hs antes)',
  cancelacion_turno: 'Cancelación de turno',
};
const EMAIL_TEMPLATE_ORDER = ['confirmacion_turno', 'recordatorio_turno', 'cancelacion_turno'];
const EMAIL_PLACEHOLDER_HELP = 'Placeholders disponibles: {patient_name}, {clinic_name}, {appointment_date}, {doctor_name}';

const EmailTemplateCard = ({ tmpl, onSaved }) => {
  const notify = useNotification();
  const [expanded, setExpanded] = useState(false);
  const [form, setForm] = useState({ subject: tmpl.subject, heading: tmpl.heading, body: tmpl.body });
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const isCustomized = tmpl.id !== 0;

  useEffect(() => {
    setForm({ subject: tmpl.subject, heading: tmpl.heading, body: tmpl.body });
  }, [tmpl]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateEmailTemplate(tmpl.template_type, form);
      notify('Plantilla actualizada', 'success');
      await onSaved();
    } catch (err) {
      notify(err.userMessage || 'Error al guardar', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm('¿Volver al texto por defecto? Se pierde la personalización actual.')) return;
    setResetting(true);
    try {
      await resetEmailTemplate(tmpl.template_type);
      notify('Plantilla restablecida al texto original', 'success');
      await onSaved();
    } catch (err) {
      notify(err.userMessage || 'Error al restablecer', 'error');
    } finally {
      setResetting(false);
    }
  };

  return (
    <Accordion expanded={expanded} onChange={(_, exp) => setExpanded(exp)} disableGutters
      sx={{ mb: 1.5, borderRadius: '8px !important', '&:before': { display: 'none' }, border: '1px solid', borderColor: 'divider' }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', flex: 1, minWidth: 0 }}>
          <EmailIcon sx={{ color: 'text.disabled', fontSize: 20 }} />
          <Typography variant="body1" fontWeight={600} sx={{ flex: 1, minWidth: 0 }}>
            {EMAIL_TEMPLATE_LABELS[tmpl.template_type]}
          </Typography>
          <Chip
            label={isCustomized ? 'Personalizada' : 'Predeterminada'}
            size="small" color={isCustomized ? 'primary' : 'default'}
            variant={isCustomized ? 'filled' : 'outlined'}
          />
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <Stack spacing={2}>
          <TextField label="Asunto del email" fullWidth size="small"
            value={form.subject} onChange={e => setForm(p => ({ ...p, subject: e.target.value }))} />
          <TextField label="Título" fullWidth size="small"
            value={form.heading} onChange={e => setForm(p => ({ ...p, heading: e.target.value }))} />
          <TextField label="Mensaje" fullWidth size="small" multiline rows={2}
            value={form.body} onChange={e => setForm(p => ({ ...p, body: e.target.value }))}
            helperText={EMAIL_PLACEHOLDER_HELP} />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, flexWrap: 'wrap' }}>
            {isCustomized && (
              <Button size="small" color="inherit" startIcon={<RestartAltIcon />}
                onClick={handleReset} disabled={resetting || saving}>
                {resetting ? <CircularProgress size={16} /> : 'Restablecer'}
              </Button>
            )}
            <Button size="small" variant="contained" onClick={handleSave} disabled={saving || resetting}>
              {saving ? <CircularProgress size={16} /> : 'Guardar'}
            </Button>
          </Box>
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
};

const EmailTemplates = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await getEmailTemplates();
      const byType = Object.fromEntries((r.data || []).map(t => [t.template_type, t]));
      setTemplates(EMAIL_TEMPLATE_ORDER.map(t => byType[t]).filter(Boolean));
    } catch { setTemplates([]); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  return (
    <Box>
      <Alert severity="info" sx={{ mb: 2 }}>
        Estos 3 emails se mandan automáticamente al paciente — al crear un turno, 24hs antes (recordatorio),
        y al cancelarlo. Podés editar el asunto y el texto; el diseño del email queda fijo.
      </Alert>
      {loading ? (
        <CircularProgress />
      ) : (
        templates.map(tmpl => <EmailTemplateCard key={tmpl.template_type} tmpl={tmpl} onSaved={load} />)
      )}
    </Box>
  );
};

// ── Plantillas WhatsApp ────────────────────────────────────────────────────

const WA_TYPES = ['confirmacion', 'recordatorio', 'cancelacion', 'custom'];

const emptyWA = { name: '', template_type: 'confirmacion', message_body: '' };

const WhatsAppTemplates = () => {
  const apiCall = useApiCall();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyWA);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');

  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuRow, setMenuRow] = useState(null);
  const openMenu = (e, t) => { setMenuAnchor(e.currentTarget); setMenuRow(t); };
  const closeMenu = () => { setMenuAnchor(null); setMenuRow(null); };

  const load = async () => {
    setLoading(true);
    try { const r = await getWhatsAppTemplates(); setTemplates(r.data || []); }
    catch { setTemplates([]); }
    finally { setLoading(false); }
  };

  const filteredTemplates = useMemo(() => {
    if (!search.trim()) return templates;
    const q = search.trim().toLowerCase();
    return templates.filter(t =>
      t.name.toLowerCase().includes(q) || (t.template_type || '').toLowerCase().includes(q)
    );
  }, [templates, search]);

  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm(emptyWA); setError(''); setOpen(true); };
  const openEdit = (t) => {
    closeMenu();
    setEditing(t);
    setForm({ name: t.name, template_type: t.template_type, message_body: t.message_body });
    setError('');
    setOpen(true);
  };

  const set = (f) => (e) => setForm(p => ({ ...p, [f]: e.target.value }));

  const handleSave = async () => {
    if (!form.name.trim() || !form.message_body.trim()) { setError('Nombre y mensaje son obligatorios'); return; }
    setSaving(true);
    setError('');
    try {
      if (editing) await updateWhatsAppTemplate(editing.id, form);
      else await createWhatsAppTemplate(form);
      setOpen(false);
      await load();
    } catch (err) {
      setError(err.userMessage || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (t) => {
    closeMenu();
    if (!window.confirm(`¿Eliminar plantilla "${t.name}"?`)) return;
    try { await apiCall(deleteWhatsAppTemplate(t.id), 'Error al eliminar'); await load(); }
    catch {}
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <Button variant="contained" onClick={openCreate}>Nueva plantilla</Button>
      </Box>

      <Paper sx={{ p: 1.5, mb: 2 }}>
        <TextField
          placeholder="Buscar por nombre o tipo…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          size="small"
          fullWidth
          InputProps={{
            startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: 'text.disabled', fontSize: 20 }} /></InputAdornment>,
          }}
        />
      </Paper>

      {loading ? <CircularProgress /> : filteredTemplates.length === 0 ? (
        <Paper sx={{ py: 6, textAlign: 'center' }}>
          <Typography color="text.secondary">
            {search ? 'Sin plantillas que coincidan con la búsqueda' : 'Sin plantillas'}
          </Typography>
        </Paper>
      ) : isMobile ? (
        <Stack spacing={1.5}>
          {filteredTemplates.map(t => (
            <Paper key={t.id} sx={{ p: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                    <Typography variant="body2" fontWeight={600}>{t.name}</Typography>
                    <Chip label={t.template_type} size="small" sx={{ height: 20, fontSize: '0.68rem' }} />
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {t.message_body}
                  </Typography>
                </Box>
                <IconButton size="small" onClick={(e) => openMenu(e, t)} sx={{ flexShrink: 0 }}>
                  <MoreVertIcon fontSize="small" />
                </IconButton>
              </Box>
            </Paper>
          ))}
        </Stack>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Nombre</TableCell>
                <TableCell>Tipo</TableCell>
                <TableCell>Mensaje (preview)</TableCell>
                <TableCell align="right" sx={{ width: 60 }}></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredTemplates.map(t => (
                <TableRow key={t.id} hover>
                  <TableCell>{t.name}</TableCell>
                  <TableCell><Chip label={t.template_type} size="small" /></TableCell>
                  <TableCell sx={{ maxWidth: 300 }}>
                    <Typography variant="caption" noWrap>{t.message_body}</Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Acciones">
                      <IconButton size="small" onClick={(e) => openMenu(e, t)}>
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={closeMenu}
        PaperProps={{ sx: { minWidth: 160 } }}>
        <MenuItem dense onClick={() => openEdit(menuRow)}>Editar</MenuItem>
        <MenuItem dense sx={{ color: 'error.main' }} onClick={() => handleDelete(menuRow)}>Eliminar</MenuItem>
      </Menu>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? 'Editar plantilla' : 'Nueva plantilla de WhatsApp'}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField label="Nombre *" fullWidth value={form.name} onChange={set('name')}
            error={!form.name.trim() && !!error} />
          <FormControl fullWidth>
            <InputLabel>Tipo</InputLabel>
            <Select value={form.template_type} label="Tipo" onChange={set('template_type')}>
              {WA_TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
            </Select>
          </FormControl>
          <TextField label="Mensaje *" fullWidth multiline rows={5}
            value={form.message_body} onChange={set('message_body')}
            error={!form.message_body.trim() && !!error}
            helperText="Placeholders disponibles: {patient_name}, {fecha}, {hora}, {doctor_name}" />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? <CircularProgress size={20} /> : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// ── Página principal ───────────────────────────────────────────────────────

const Templates = () => {
  const { isAdmin } = useAuth();
  const { features } = useClinicFeatures();
  const [tab, setTab] = useState(0);

  // Array de tabs disponibles en vez de índices hardcodeados (tab === 0,
  // tab === 1, ...) — con condiciones de visibilidad distintas por tab
  // (rol, feature flag), un índice fijo se desalinea apenas una condición
  // cambia de estado; acá el índice seleccionado siempre corresponde a la
  // posición real dentro de las tabs que están efectivamente visibles.
  const availableTabs = [
    { key: 'consultation', label: 'Consultas médicas', Component: ConsultationTemplates },
    ...(features.study_orders ? [{ key: 'study-orders', label: 'Órdenes de estudio', Component: StudyOrderTemplates }] : []),
    ...(isAdmin ? [{ key: 'whatsapp', label: 'WhatsApp', Component: WhatsAppTemplates }] : []),
    ...(isAdmin ? [{ key: 'email', label: 'Email', Component: EmailTemplates }] : []),
  ];
  const activeTab = availableTabs[tab] || availableTabs[0];
  const ActiveComponent = activeTab.Component;

  return (
    <Box>
      <Typography variant="h4" gutterBottom>Plantillas</Typography>
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3 }}>
        {availableTabs.map(t => <Tab key={t.key} label={t.label} />)}
      </Tabs>
      <ActiveComponent />
    </Box>
  );
};

export default Templates;
